import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { fetchAllPages } from "@/lib/supabasePaging";
import { subscribeWithResync, uniqueChannelName } from "@/lib/realtimeResync";
import {
  CYCLE_COLUMNS,
  LOG_COLUMNS,
  QUOTE_COLUMNS,
  pausePatch,
  phoneCallLog,
  resumePatch,
  sendNowPatch,
  sortLogs,
  type CycleRow,
  type QuoteRow,
  type ReminderLog,
} from "./caseRelances";

export type RelanceAction = "call" | "pause" | "resume" | "send-now" | "retry";

export interface CaseRelancesState {
  cycle: CycleRow | null;
  logs: ReminderLog[];
  quotes: QuoteRow[];
  loading: boolean;
  error: string | null;
  actionError: string | null;
  busyAction: RelanceAction | null;
  reload: () => Promise<void>;
  run: (action: RelanceAction) => Promise<boolean>;
}

const message = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : typeof err === "object" && err && "message" in err ? String((err as { message: unknown }).message) : fallback;

/**
 * Cycle de relance devis, journal des relances et devis suivis d'UN dossier.
 * Toutes les requêtes sont filtrées sur case_id ; realtime sur quote_reminder_cycles et reminder_logs,
 * rechargement après chaque action et quand le statut du dossier change (les triggers SQL arrêtent le cycle).
 */
export function useCaseRelances(caseId: string, caseStatus: string): CaseRelancesState {
  const [cycle, setCycle] = useState<CycleRow | null>(null);
  const [logs, setLogs] = useState<ReminderLog[]>([]);
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<RelanceAction | null>(null);
  const requestId = useRef(0);

  const reload = useCallback(async () => {
    const id = ++requestId.current;
    try {
      setError(null);
      const [cycles, logRows] = await Promise.all([
        fetchAllPages<CycleRow>((from, to) =>
          supabase
            .from("quote_reminder_cycles")
            .select(CYCLE_COLUMNS)
            .eq("case_id", caseId)
            .order("created_at", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to)
            .returns<CycleRow[]>()
        ),
        fetchAllPages<ReminderLog>((from, to) =>
          supabase
            .from("reminder_logs")
            .select(LOG_COLUMNS)
            .eq("reminder_type", "quote_no_reply")
            .eq("case_id", caseId)
            .order("sent_at", { ascending: false })
            .order("id", { ascending: true })
            .range(from, to)
            .returns<ReminderLog[]>()
        ),
      ]);
      // Un seul cycle par dossier (contrainte UNIQUE(case_id)) ; on garde le plus récent par prudence.
      const current = cycles[0] ?? null;
      const quoteIds = current && Array.isArray(current.quote_ids) ? current.quote_ids : [];
      const quoteRows = quoteIds.length
        ? await fetchAllPages<QuoteRow>((from, to) =>
            supabase
              .from("quotes")
              .select(QUOTE_COLUMNS)
              .in("id", quoteIds)
              .is("archived_at", null)
              .order("id", { ascending: true })
              .range(from, to)
              .returns<QuoteRow[]>()
          )
        : [];
      if (id !== requestId.current) return;
      setCycle(current);
      setLogs(sortLogs(logRows));
      setQuotes(quoteRows);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("[relances] chargement du dossier impossible", err);
      setError(message(err, "Impossible de charger les relances de ce dossier."));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    setLoading(true);
    setCycle(null);
    setLogs([]);
    setQuotes([]);
    void reload();
    // Nom unique : un démontage / remontage rapide du même dossier ne récupère pas le channel en
    // cours de fermeture. Resynchronisation à la reconnexion et au retour sur l'onglet.
    const channel = supabase
      .channel(uniqueChannelName(`case-relances:${caseId}`))
      .on("postgres_changes", { event: "*", schema: "public", table: "quote_reminder_cycles", filter: `case_id=eq.${caseId}` }, () => {
        void reload();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "reminder_logs", filter: `case_id=eq.${caseId}` }, () => {
        void reload();
      });
    return subscribeWithResync(channel, () => void reload());
  }, [caseId, reload]);

  // Carte déplacée / signée : le trigger SQL arrête le cycle, on relit après l'auto-save du drawer.
  const firstStatus = useRef(true);
  useEffect(() => {
    if (firstStatus.current) {
      firstStatus.current = false;
      return;
    }
    const timer = window.setTimeout(() => void reload(), 1500);
    return () => window.clearTimeout(timer);
  }, [caseStatus, reload]);

  const run = useCallback(
    async (action: RelanceAction) => {
      if (!cycle || busyAction) return false;
      setBusyAction(action);
      setActionError(null);
      try {
        if (action === "call") {
          const { error: logError } = await supabase.from("reminder_logs").insert(phoneCallLog(cycle));
          if (logError) throw logError;
        } else {
          const patch = action === "pause" ? pausePatch() : action === "resume" ? resumePatch() : sendNowPatch();
          const { error: updateError } = await supabase.from("quote_reminder_cycles").update(patch).eq("id", cycle.id);
          if (updateError) throw updateError;
        }
        await reload();
        return true;
      } catch (err) {
        console.error("[relances] action impossible", err);
        setActionError(message(err, "L’action n’a pas abouti. Réessayez."));
        return false;
      } finally {
        setBusyAction(null);
      }
    },
    [busyAction, cycle, reload]
  );

  return { cycle, logs, quotes, loading, error, actionError, busyAction, reload, run };
}
